import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { useAuth } from '../../context/AuthContext';
import SuperAdminNav from '../../components/SuperAdminNav';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const MERGE_FIELDS = [
  { label: 'First Name', token: '{First Name|there}' },
  { label: 'Last Name', token: '{Last Name|}' },
  { label: 'Team Name', token: '{Team Name|your team}' },
  { label: 'Club Name', token: '{Club Name|your club}' },
];

const darkStyles = `
  .email-editor-quill .ql-toolbar {
    background: #1a1a1a;
    border-color: #2a2a2a;
  }
  .email-editor-quill .ql-container {
    background: #1a1a1a;
    border-color: #2a2a2a;
    color: white;
    min-height: 300px;
  }
  .email-editor-quill .ql-editor {
    min-height: 300px;
    color: #fff;
  }
  .email-editor-quill .ql-editor p,
  .email-editor-quill .ql-editor li,
  .email-editor-quill .ql-editor h1,
  .email-editor-quill .ql-editor h2,
  .email-editor-quill .ql-editor h3 {
    color: #fff;
  }
  .email-editor-quill .ql-snow .ql-stroke {
    stroke: #ccc;
  }
  .email-editor-quill .ql-snow .ql-fill {
    fill: #ccc;
  }
  .email-editor-quill .ql-snow .ql-picker-label {
    color: #ccc;
  }
  .email-editor-quill .ql-snow .ql-picker-options {
    background: #1a1a1a;
    border-color: #2a2a2a;
  }
  .email-editor-quill .ql-snow .ql-picker-item {
    color: #ccc;
  }
  .email-editor-quill .ql-snow .ql-picker-item:hover {
    color: white;
  }
  .email-editor-quill .ql-editor.ql-blank::before {
    color: #666;
  }
  .email-editor-quill .ql-snow a {
    color: #f77c00;
  }
  .email-editor-quill .ql-tooltip {
    background: #1a1a1a;
    border-color: #2a2a2a;
    color: #ccc;
    box-shadow: 0 2px 8px rgba(0,0,0,0.4);
  }
  .email-editor-quill .ql-tooltip input[type="text"] {
    background: #0a0a0a;
    border-color: #2a2a2a;
    color: white;
  }
  .email-editor-quill .ql-snow .ql-color-picker .ql-picker-options,
  .email-editor-quill .ql-snow .ql-background .ql-picker-options {
    background: #1a1a1a;
    border-color: #2a2a2a;
    padding: 4px;
  }
  .email-editor-quill .ql-snow .ql-picker.ql-align .ql-picker-options {
    background: #1a1a1a;
    border-color: #2a2a2a;
  }
`;

export default function EmailEditor() {
  const { apiFetch } = useAuth();
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const quillRef = useRef(null);

  // Determine mode from URL
  const isCreateMode = location.pathname.includes('/new/');
  const emailType = isCreateMode ? params.type : null;
  const emailId = isCreateMode ? null : params.id;

  // Form state
  const [name, setName] = useState('');
  const [recipientType, setRecipientType] = useState('coach');
  const [subject, setSubject] = useState('');
  const [preheader, setPreheader] = useState('');
  const [fromName, setFromName] = useState('');
  const [htmlBody, setHtmlBody] = useState('');

  // Onboarding fields
  const [dayNumber, setDayNumber] = useState(1);
  const [onboardingSendTime, setOnboardingSendTime] = useState('08:00');
  const [skipMondays, setSkipMondays] = useState(true);

  // Timed fields
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [frequency, setFrequency] = useState('weekly');
  const [firstSendDate, setFirstSendDate] = useState('');
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [timedSendTime, setTimedSendTime] = useState('10:00');
  const [minAccountAgeDays, setMinAccountAgeDays] = useState('');

  // Broadcast fields
  const [sendMode, setSendMode] = useState('now');
  const [broadcastDate, setBroadcastDate] = useState('');
  const [broadcastTime, setBroadcastTime] = useState('10:00');

  // Triggered fields (edit only)
  const [triggerName, setTriggerName] = useState('');
  const [triggeredSendTime, setTriggeredSendTime] = useState('');

  // Loaded email data
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(!isCreateMode);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Preview state
  const [recipients, setRecipients] = useState([]);
  const [selectedRecipientId, setSelectedRecipientId] = useState('sample');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  // Test email state
  const [testEmailAddr, setTestEmailAddr] = useState('matt@outsmartpress.com');
  const [testSending, setTestSending] = useState(false);
  const [testMsg, setTestMsg] = useState('');

  // Merge field dropdown
  const [showMergeDropdown, setShowMergeDropdown] = useState(false);

  // Insert button form
  const [showButtonForm, setShowButtonForm] = useState(false);
  const [buttonText, setButtonText] = useState('');
  const [buttonUrl, setButtonUrl] = useState('');

  // Broadcast send state
  const [broadcasting, setBroadcasting] = useState(false);
  const [suppressEmails, setSuppressEmails] = useState(false);
  const [suppressEmailList, setSuppressEmailList] = useState('');

  const currentType = email?.type || emailType;

  // Upload an image file and return its hosted URL
  const uploadImage = useCallback(async (file) => {
    if (!file.type.startsWith('image/')) throw new Error('Not an image file');
    if (file.size > 1024 * 1024) throw new Error('Image must be under 1MB');
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const resp = await apiFetch('/api/super/email-images', {
            method: 'POST',
            body: JSON.stringify({
              data: reader.result,
              filename: file.name,
              content_type: file.type,
            }),
          });
          resolve(resp.url);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }, [apiFetch]);

  // Custom image handler: opens file picker, uploads, inserts hosted URL
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/gif,image/webp';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const url = await uploadImage(file);
        const editor = quillRef.current?.getEditor();
        if (editor) {
          const range = editor.getSelection(true);
          editor.insertEmbed(range.index, 'image', url);
          editor.setSelection(range.index + 1);
        }
      } catch (err) {
        showMsg(err.message || 'Image upload failed', true);
      }
    };
    input.click();
  }, [uploadImage]);

  // Quill modules with custom image handler
  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['link', 'image'],
        ['clean'],
      ],
      handlers: {
        image: imageHandler,
      },
    },
  }), [imageHandler]);

  const showMsg = (msg, isError) => {
    if (isError) {
      setErrorMsg(msg);
      setTimeout(() => setErrorMsg(''), 5000);
    } else {
      setSuccessMsg(msg);
      setTimeout(() => setSuccessMsg(''), 5000);
    }
  };

  // Load email on edit mode
  const loadEmail = useCallback(async () => {
    if (!emailId) return;
    setLoading(true);
    try {
      const data = await apiFetch(`/api/super/emails/${emailId}`);
      const e = data.email;
      setEmail(e);
      setName(e.internal_name || '');
      setRecipientType(e.recipient_type || 'coach');
      setSubject(e.subject || '');
      setPreheader(e.preheader || '');
      setFromName(e.from_name_override || '');
      setHtmlBody(e.body_html || '');

      if (e.type === 'onboarding') {
        setDayNumber(e.day_number || 1);
        setOnboardingSendTime(e.send_time || '08:00');
        setSkipMondays(e.skip_mondays !== false);
      } else if (e.type === 'timed') {
        setDayOfWeek(e.day_of_week != null ? e.day_of_week : 1);
        setFrequency(e.frequency || 'weekly');
        setFirstSendDate(e.first_send_date || '');
        setDayOfMonth(e.day_of_month != null ? e.day_of_month : 1);
        setTimedSendTime(e.send_time || '10:00');
        setMinAccountAgeDays(e.min_account_age_days != null ? String(e.min_account_age_days) : '');
      } else if (e.type === 'broadcast') {
        if (e.send_mode === 'schedule') {
          setSendMode('schedule');
          setBroadcastDate(e.scheduled_date || '');
          setBroadcastTime(e.scheduled_time || '08:00');
        } else {
          setSendMode('now');
        }
      } else if (e.type === 'triggered') {
        setTriggerName(e.trigger_name || '');
        setTriggeredSendTime(e.send_time || '');
      }
    } catch (err) {
      showMsg(err.message || 'Failed to load email', true);
    } finally {
      setLoading(false);
    }
  }, [emailId, apiFetch]);

  useEffect(() => {
    loadEmail();
  }, [loadEmail]);

  // Load recipients for preview
  const loadRecipients = useCallback(async () => {
    const type = currentType;
    if (!type) return;
    // For the recipient type, use what's selected in the form
    try {
      const data = await apiFetch(`/api/super/emails/recipients/${recipientType}`);
      setRecipients(data.recipients || []);
    } catch (err) {
      console.error('Failed to load recipients:', err);
    }
  }, [apiFetch, recipientType, currentType]);

  useEffect(() => {
    loadRecipients();
  }, [loadRecipients]);

  // Load preview
  const loadPreview = useCallback(async () => {
    if (!emailId) return;
    setPreviewLoading(true);
    try {
      const qs = selectedRecipientId !== 'sample' ? `?recipient_id=${selectedRecipientId}` : '';
      const data = await apiFetch(`/api/super/emails/${emailId}/preview${qs}`);
      setPreviewSubject(data.subject || '');
      setPreviewHtml(data.html || '');
    } catch (err) {
      console.error('Preview failed:', err);
      setPreviewHtml('<p style="color:#ef4444;">Preview failed to load.</p>');
    } finally {
      setPreviewLoading(false);
    }
  }, [emailId, selectedRecipientId, apiFetch]);

  useEffect(() => {
    if (emailId) {
      loadPreview();
    }
  }, [loadPreview, emailId]);

  // Intercept paste and drop events to upload images instead of embedding base64
  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const handleImage = async (file) => {
      try {
        const url = await uploadImage(file);
        const range = editor.getSelection(true);
        editor.insertEmbed(range.index, 'image', url);
        editor.setSelection(range.index + 1);
      } catch (err) {
        showMsg(err.message || 'Image upload failed', true);
      }
    };

    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          e.preventDefault();
          e.stopPropagation();
          const file = items[i].getAsFile();
          if (file) handleImage(file);
          return;
        }
      }
    };

    const handleDrop = (e) => {
      const files = e.dataTransfer?.files;
      if (!files || files.length === 0) return;
      const imageFile = Array.from(files).find(f => f.type.startsWith('image/'));
      if (!imageFile) return;
      e.preventDefault();
      e.stopPropagation();
      handleImage(imageFile);
    };

    editor.root.addEventListener('paste', handlePaste, { capture: true });
    editor.root.addEventListener('drop', handleDrop, { capture: true });
    return () => {
      editor.root.removeEventListener('paste', handlePaste, { capture: true });
      editor.root.removeEventListener('drop', handleDrop, { capture: true });
    };
  }, [uploadImage]);

  // Build request body
  const buildBody = () => {
    const body = {
      internal_name: name.trim(),
      recipient_type: recipientType,
      subject: subject.trim(),
      preheader: preheader.trim(),
      from_name_override: fromName.trim() || null,
      body_html: htmlBody,
    };

    if (isCreateMode) {
      body.type = emailType;
    }

    if (currentType === 'onboarding') {
      body.day_number = parseInt(dayNumber, 10);
      body.send_time = onboardingSendTime;
      body.skip_mondays = skipMondays;
    } else if (currentType === 'timed') {
      body.frequency = frequency;
      if (frequency === 'monthly') {
        body.day_of_month = parseInt(dayOfMonth, 10);
        body.day_of_week = null;
      } else {
        body.day_of_week = parseInt(dayOfWeek, 10);
        body.day_of_month = null;
      }
      if (frequency === 'biweekly' && firstSendDate) {
        body.first_send_date = firstSendDate;
      }
      body.send_time = timedSendTime;
      body.min_account_age_days = minAccountAgeDays ? parseInt(minAccountAgeDays, 10) : null;
    } else if (currentType === 'broadcast') {
      body.send_mode = sendMode === 'schedule' ? 'schedule' : 'send_now';
      if (sendMode === 'schedule' && broadcastDate) {
        body.scheduled_date = broadcastDate;
        body.scheduled_time = broadcastTime || '08:00';
      }
    } else if (currentType === 'triggered') {
      body.send_time = triggeredSendTime || null;
    }

    return body;
  };

  // Save handler
  const handleSave = async () => {
    if (!name.trim()) {
      showMsg('Internal name is required.', true);
      return;
    }
    setSaving(true);
    try {
      if (isCreateMode) {
        const data = await apiFetch('/api/super/emails', {
          method: 'POST',
          body: JSON.stringify(buildBody()),
        });
        const newId = data.email?.id || data.id;
        navigate(`/super/emails/${newId}`, { replace: true });
        showMsg('Email created.');
      } else {
        await apiFetch(`/api/super/emails/${emailId}`, {
          method: 'PUT',
          body: JSON.stringify(buildBody()),
        });
        showMsg('Email saved.');
        loadEmail();
      }
    } catch (err) {
      showMsg(err.message || 'Save failed.', true);
    } finally {
      setSaving(false);
    }
  };

  // Send test
  const handleSendTest = async () => {
    if (!testEmailAddr.trim()) return;
    setTestSending(true);
    setTestMsg('');
    try {
      const body = { to: testEmailAddr.trim() };
      if (selectedRecipientId !== 'sample') {
        body.recipient_id = selectedRecipientId;
      }
      const data = await apiFetch(`/api/super/emails/${emailId}/send-test`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setTestMsg(data.message || 'Test email sent.');
      setTimeout(() => setTestMsg(''), 5000);
    } catch (err) {
      setTestMsg(err.message || 'Failed to send test.');
      setTimeout(() => setTestMsg(''), 5000);
    } finally {
      setTestSending(false);
    }
  };

  // Send broadcast
  const handleSendBroadcast = async () => {
    if (!window.confirm('Send this broadcast email to all matching recipients? This cannot be undone.')) return;
    setBroadcasting(true);
    try {
      const body = {};
      if (suppressEmails && suppressEmailList.trim()) {
        body.suppress_emails = suppressEmailList.split(',').map(e => e.trim().toLowerCase()).filter(Boolean);
      }
      const data = await apiFetch(`/api/super/emails/${emailId}/send-broadcast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      showMsg(data.message || 'Broadcast sent.');
      loadEmail();
    } catch (err) {
      showMsg(err.message || 'Broadcast failed.', true);
    } finally {
      setBroadcasting(false);
    }
  };

  // Archive
  const handleArchive = async () => {
    if (!window.confirm('Archive this email? It will no longer be sent.')) return;
    try {
      await apiFetch(`/api/super/emails/${emailId}/archive`, {
        method: 'POST',
      });
      showMsg('Email archived.');
      navigate('/super/emails');
    } catch (err) {
      showMsg(err.message || 'Archive failed.', true);
    }
  };

  // Insert merge field into Quill
  const insertMergeField = (token) => {
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection(true);
      editor.insertText(range.index, token);
      editor.setSelection(range.index + token.length);
    }
    setShowMergeDropdown(false);
  };

  // Insert button HTML into Quill
  const insertButton = () => {
    if (!buttonText.trim() || !buttonUrl.trim()) return;
    const html = `<a href="${buttonUrl.trim()}" style="display:inline-block;padding:10px 28px;background:#f77c00;color:#ffffff;text-decoration:none;border-radius:8px;font-weight:bold;font-size:16px;">${buttonText.trim()}</a>`;
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection(true);
      editor.clipboard.dangerouslyPasteHTML(range.index, html);
    }
    setButtonText('');
    setButtonUrl('');
    setShowButtonForm(false);
  };

  // Determine if archive is allowed
  const canArchive = () => {
    if (!email) return false;
    if (email.type === 'triggered') return false;
    if (email.type === 'broadcast' && email.sent_at) return false;
    return true;
  };

  // Determine if broadcast send is allowed
  const canSendBroadcast = () => {
    if (!email) return false;
    if (email.type !== 'broadcast') return false;
    if (email.sent_at) return false;
    return true;
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 14px',
    background: '#1a1a1a',
    border: '1px solid #2a2a2a',
    borderRadius: 10,
    color: 'white',
    fontSize: 16,
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const selectStyle = {
    ...inputStyle,
    appearance: 'auto',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: 600,
    marginBottom: 6,
    color: '#888',
  };

  const cardStyle = {
    background: '#1a1a1a',
    border: '1px solid #2a2a2a',
    borderRadius: 12,
    padding: '20px 24px',
    marginBottom: 20,
  };

  const sectionTitleStyle = {
    fontSize: '1rem',
    fontWeight: 700,
    marginBottom: 16,
    color: 'white',
  };

  if (loading) {
    return (
      <div className="admin-layout">
        <SuperAdminNav activeItem="emails" />
        <div className="page">
          <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>Loading...</div>
        </div>
      </div>
    );
  }

  const typeLabel = currentType ? currentType.charAt(0).toUpperCase() + currentType.slice(1) : '';

  return (
    <div className="admin-layout">
      <SuperAdminNav activeItem="emails" />
      <style>{darkStyles}</style>
      <div className="page" style={{ maxWidth: 900, margin: '0 auto', paddingBottom: 60 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 className="page-title" style={{ marginBottom: 4 }}>
              {isCreateMode ? `New ${typeLabel} Email` : `Edit ${typeLabel} Email`}
            </h1>
            {email?.archived && (
              <span style={{ fontSize: '0.8rem', background: '#333', color: '#f77c00', padding: '2px 8px', borderRadius: 6 }}>
                Archived
              </span>
            )}
            {email?.sent_at && email.type === 'broadcast' && (
              <span style={{ fontSize: '0.8rem', background: '#1a3a1a', color: '#22c55e', padding: '2px 8px', borderRadius: 6, marginLeft: 8 }}>
                Sent {new Date(email.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </span>
            )}
          </div>
          <button
            onClick={() => navigate('/super/emails')}
            style={{ background: 'transparent', border: '1px solid #2a2a2a', color: '#ccc', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem' }}
          >
            Back to Emails
          </button>
        </div>

        {/* Success/Error messages */}
        {successMsg && (
          <div style={{ background: '#1a3a1a', border: '1px solid #22c55e', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: '0.9em', color: '#22c55e' }}>
            {successMsg}
          </div>
        )}
        {errorMsg && (
          <div style={{ background: '#3a1a1a', border: '1px solid #ef4444', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: '0.9em', color: '#ef4444' }}>
            {errorMsg}
          </div>
        )}

        {/* Core fields */}
        <div style={cardStyle}>
          <div style={sectionTitleStyle}>Email Details</div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Internal Name *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Welcome Email Day 1"
              style={inputStyle}
            />
          </div>

          <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={labelStyle}>Recipient Type</label>
              <select
                value={recipientType}
                onChange={e => setRecipientType(e.target.value)}
                disabled={!isCreateMode && currentType === 'triggered'}
                style={selectStyle}
              >
                <option value="coach">Coach</option>
                <option value="club_pp">Club Admin (Parent Pay)</option>
                <option value="club_cp">Club Admin (Club Pays)</option>
                <option value="parent_cp">Parent (Club Pay)</option>
                <option value="parent_pp">Parent (Parent Pay)</option>
                <option value="parent_ind">Parent (Individual/Family)</option>
              </select>
            </div>
            <div style={{ flex: 2, minWidth: 300 }}>
              <label style={labelStyle}>Subject Line</label>
              <input
                type="text"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="Email subject"
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={labelStyle}>Preheader <span style={{ fontWeight: 400, color: '#666' }}>(optional)</span></label>
              <input
                type="text"
                value={preheader}
                onChange={e => setPreheader(e.target.value)}
                placeholder="Preview text shown in inbox"
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={labelStyle}>From Name Override</label>
              <input
                type="text"
                value={fromName}
                onChange={e => setFromName(e.target.value)}
                placeholder="Train Daily"
                style={inputStyle}
              />
            </div>
          </div>
        </div>

        {/* Type-specific fields */}
        {currentType === 'onboarding' && (
          <div style={cardStyle}>
            <div style={sectionTitleStyle}>Onboarding Settings</div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 120 }}>
                <label style={labelStyle}>Day Number</label>
                <input
                  type="number"
                  min="1"
                  value={dayNumber}
                  onChange={e => setDayNumber(e.target.value)}
                  style={{ ...inputStyle, width: 120 }}
                />
              </div>
              <div style={{ minWidth: 140 }}>
                <label style={labelStyle}>Send Time</label>
                <input
                  type="time"
                  value={onboardingSendTime}
                  onChange={e => setOnboardingSendTime(e.target.value)}
                  style={{ ...inputStyle, width: 160 }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 4 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem', color: '#ccc' }}>
                  <input
                    type="checkbox"
                    checked={skipMondays}
                    onChange={e => setSkipMondays(e.target.checked)}
                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                  />
                  Skip Mondays
                </label>
              </div>
            </div>
          </div>
        )}

        {currentType === 'timed' && (
          <div style={cardStyle}>
            <div style={sectionTitleStyle}>Timed Settings</div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
              <div style={{ minWidth: 140 }}>
                <label style={labelStyle}>Frequency</label>
                <select
                  value={frequency}
                  onChange={e => setFrequency(e.target.value)}
                  style={{ ...selectStyle, width: 160 }}
                >
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Biweekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              {frequency !== 'monthly' && (
                <div style={{ minWidth: 160 }}>
                  <label style={labelStyle}>Day of Week</label>
                  <select
                    value={dayOfWeek}
                    onChange={e => setDayOfWeek(e.target.value)}
                    style={{ ...selectStyle, width: 180 }}
                  >
                    {DAYS_OF_WEEK.map(d => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>
              )}
              {frequency === 'monthly' && (
                <div style={{ minWidth: 160 }}>
                  <label style={labelStyle}>Day of Month</label>
                  <select
                    value={dayOfMonth}
                    onChange={e => setDayOfMonth(e.target.value)}
                    style={{ ...selectStyle, width: 120 }}
                  >
                    {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
              )}
              <div style={{ minWidth: 140 }}>
                <label style={labelStyle}>Send Time</label>
                <input
                  type="time"
                  value={timedSendTime}
                  onChange={e => setTimedSendTime(e.target.value)}
                  style={{ ...inputStyle, width: 160 }}
                />
              </div>
            </div>
            {frequency === 'biweekly' && (
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>First Send Date</label>
                <input
                  type="date"
                  value={firstSendDate}
                  onChange={e => setFirstSendDate(e.target.value)}
                  style={{ ...inputStyle, width: 200 }}
                />
              </div>
            )}
            <div style={{ maxWidth: 200 }}>
              <label style={labelStyle}>Min Account Age (days) <span style={{ fontWeight: 400, color: '#666' }}>(optional)</span></label>
              <input
                type="number"
                min="0"
                value={minAccountAgeDays}
                onChange={e => setMinAccountAgeDays(e.target.value)}
                placeholder="e.g. 7"
                style={{ ...inputStyle, width: 120 }}
              />
            </div>
          </div>
        )}

        {currentType === 'broadcast' && (
          <div style={cardStyle}>
            <div style={sectionTitleStyle}>Broadcast Settings</div>
            <div style={{ display: 'flex', gap: 24, marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: '#ccc', fontSize: '0.9rem' }}>
                <input
                  type="radio"
                  name="sendMode"
                  value="now"
                  checked={sendMode === 'now'}
                  onChange={() => setSendMode('now')}
                  style={{ width: 18, height: 18 }}
                />
                Send Now
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: '#ccc', fontSize: '0.9rem' }}>
                <input
                  type="radio"
                  name="sendMode"
                  value="schedule"
                  checked={sendMode === 'schedule'}
                  onChange={() => setSendMode('schedule')}
                  style={{ width: 18, height: 18 }}
                />
                Schedule
              </label>
            </div>
            {sendMode === 'schedule' && (
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <div>
                  <label style={labelStyle}>Date</label>
                  <input
                    type="date"
                    value={broadcastDate}
                    onChange={e => setBroadcastDate(e.target.value)}
                    style={{ ...inputStyle, width: 200 }}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Time</label>
                  <input
                    type="time"
                    value={broadcastTime}
                    onChange={e => setBroadcastTime(e.target.value)}
                    style={{ ...inputStyle, width: 160 }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {currentType === 'triggered' && !isCreateMode && (
          <div style={cardStyle}>
            <div style={sectionTitleStyle}>Trigger Settings</div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Trigger Name</label>
              <div style={{ padding: '12px 14px', background: '#0a0a0a', border: '1px solid #2a2a2a', borderRadius: 10, color: '#ccc', fontSize: '0.95rem' }}>
                {triggerName || '—'}
              </div>
            </div>
            <div style={{ maxWidth: 200 }}>
              <label style={labelStyle}>Send Time <span style={{ fontWeight: 400, color: '#666' }}>(if applicable)</span></label>
              <input
                type="time"
                value={triggeredSendTime}
                onChange={e => setTriggeredSendTime(e.target.value)}
                style={{ ...inputStyle, width: 160 }}
              />
            </div>
          </div>
        )}

        {/* Rich Text Editor */}
        <div style={cardStyle}>
          <div style={sectionTitleStyle}>Email Body</div>

          <div className="email-editor-quill" style={{ marginBottom: 12 }}>
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={htmlBody}
              onChange={setHtmlBody}
              modules={quillModules}
              placeholder="Write your email content here..."
            />
          </div>

          {/* Custom toolbar controls below editor toolbar */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {/* Merge Field Dropdown */}
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => { setShowMergeDropdown(!showMergeDropdown); setShowButtonForm(false); }}
                style={{
                  background: '#2a2a2a',
                  border: '1px solid #3a3a3a',
                  color: '#ccc',
                  padding: '8px 14px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontFamily: 'inherit',
                }}
              >
                Insert Merge Field {showMergeDropdown ? '▲' : '▼'}
              </button>
              {showMergeDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: 4,
                  background: '#1a1a1a',
                  border: '1px solid #2a2a2a',
                  borderRadius: 8,
                  zIndex: 10,
                  minWidth: 180,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                }}>
                  {MERGE_FIELDS.map(field => (
                    <button
                      key={field.label}
                      type="button"
                      onClick={() => insertMergeField(field.token)}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        padding: '10px 14px',
                        background: 'transparent',
                        border: 'none',
                        color: '#ccc',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontFamily: 'inherit',
                        borderBottom: '1px solid #2a2a2a',
                      }}
                      onMouseEnter={e => { e.target.style.background = '#2a2a2a'; }}
                      onMouseLeave={e => { e.target.style.background = 'transparent'; }}
                    >
                      {field.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Insert Button */}
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => { setShowButtonForm(!showButtonForm); setShowMergeDropdown(false); }}
                style={{
                  background: '#2a2a2a',
                  border: '1px solid #3a3a3a',
                  color: '#ccc',
                  padding: '8px 14px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontFamily: 'inherit',
                }}
              >
                Insert Button
              </button>
              {showButtonForm && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: 4,
                  background: '#1a1a1a',
                  border: '1px solid #2a2a2a',
                  borderRadius: 8,
                  zIndex: 10,
                  padding: 16,
                  minWidth: 280,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                }}>
                  <div style={{ marginBottom: 10 }}>
                    <label style={{ ...labelStyle, fontSize: '0.8rem' }}>Button Text</label>
                    <input
                      type="text"
                      value={buttonText}
                      onChange={e => setButtonText(e.target.value)}
                      placeholder="e.g. Get Started"
                      style={{ ...inputStyle, padding: '8px 10px', fontSize: 14 }}
                    />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ ...labelStyle, fontSize: '0.8rem' }}>Button URL</label>
                    <input
                      type="text"
                      value={buttonUrl}
                      onChange={e => setButtonUrl(e.target.value)}
                      placeholder="https://..."
                      style={{ ...inputStyle, padding: '8px 10px', fontSize: 14 }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={insertButton}
                    disabled={!buttonText.trim() || !buttonUrl.trim()}
                    style={{
                      background: '#f77c00',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: 6,
                      cursor: !buttonText.trim() || !buttonUrl.trim() ? 'not-allowed' : 'pointer',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      fontFamily: 'inherit',
                      opacity: !buttonText.trim() || !buttonUrl.trim() ? 0.5 : 1,
                    }}
                  >
                    Insert
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Preview panel */}
        <div style={cardStyle}>
          <div style={sectionTitleStyle}>Preview</div>

          {isCreateMode && !emailId ? (
            <div style={{ color: '#888', fontSize: '0.9rem' }}>Save first to preview.</div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', marginBottom: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <label style={labelStyle}>Preview As</label>
                  <select
                    value={selectedRecipientId}
                    onChange={e => setSelectedRecipientId(e.target.value)}
                    style={selectStyle}
                  >
                    <option value="sample">Sample Data</option>
                    {recipients.map(r => (
                      <option key={r.id} value={r.id}>
                        {r.first_name} {r.last_name} ({r.email})
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={loadPreview}
                  disabled={previewLoading}
                  style={{
                    background: '#2a2a2a',
                    border: '1px solid #3a3a3a',
                    color: '#ccc',
                    padding: '10px 20px',
                    borderRadius: 8,
                    cursor: previewLoading ? 'not-allowed' : 'pointer',
                    fontSize: '0.85rem',
                    fontFamily: 'inherit',
                  }}
                >
                  {previewLoading ? 'Loading...' : 'Refresh Preview'}
                </button>
              </div>

              {previewSubject && (
                <div style={{ marginBottom: 12 }}>
                  <span style={{ fontSize: '0.8rem', color: '#888' }}>Subject: </span>
                  <span style={{ fontSize: '0.9rem', color: '#ccc' }}>{previewSubject}</span>
                </div>
              )}

              <div
                style={{
                  background: 'white',
                  borderRadius: 8,
                  padding: 20,
                  minHeight: 200,
                  maxHeight: 600,
                  overflow: 'auto',
                }}
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </>
          )}
        </div>

        {/* Send Test Email */}
        {!isCreateMode && emailId && (
          <div style={cardStyle}>
            <div style={sectionTitleStyle}>Send Test Email</div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 250 }}>
                <label style={labelStyle}>Send To</label>
                <input
                  type="email"
                  value={testEmailAddr}
                  onChange={e => setTestEmailAddr(e.target.value)}
                  placeholder="test@example.com"
                  style={inputStyle}
                />
              </div>
              <button
                type="button"
                onClick={handleSendTest}
                disabled={testSending || !testEmailAddr.trim()}
                style={{
                  background: '#2a2a2a',
                  border: '1px solid #3a3a3a',
                  color: '#ccc',
                  padding: '12px 24px',
                  borderRadius: 8,
                  cursor: testSending || !testEmailAddr.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  whiteSpace: 'nowrap',
                }}
              >
                {testSending ? 'Sending...' : 'Send Test'}
              </button>
            </div>
            {testMsg && (
              <div style={{ marginTop: 10, fontSize: '0.85rem', color: testMsg.toLowerCase().includes('fail') ? '#ef4444' : '#22c55e' }}>
                {testMsg}
              </div>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{
              background: '#f77c00',
              color: 'white',
              border: 'none',
              padding: '14px 32px',
              borderRadius: 10,
              cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: 700,
              fontFamily: 'inherit',
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>

          {!isCreateMode && canSendBroadcast() && (
            <>
              <button
                type="button"
                onClick={handleSendBroadcast}
                disabled={broadcasting}
                style={{
                  background: '#22c55e',
                  color: 'white',
                  border: 'none',
                  padding: '14px 28px',
                  borderRadius: 10,
                  cursor: broadcasting ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: 700,
                  fontFamily: 'inherit',
                  opacity: broadcasting ? 0.6 : 1,
                }}
              >
                {broadcasting ? 'Sending...' : 'Send Broadcast'}
              </button>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.9rem', color: '#ccc' }}>
                <input
                  type="checkbox"
                  checked={suppressEmails}
                  onChange={(e) => setSuppressEmails(e.target.checked)}
                  style={{ accentColor: '#f77c00' }}
                />
                Suppress Emails
              </label>
            </>
          )}

          {!isCreateMode && canArchive() && (
            <button
              type="button"
              onClick={handleArchive}
              style={{
                background: 'transparent',
                border: '1px solid #ef4444',
                color: '#ef4444',
                padding: '14px 28px',
                borderRadius: 10,
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 600,
                fontFamily: 'inherit',
              }}
            >
              Archive
            </button>
          )}
        </div>

        {suppressEmails && !isCreateMode && canSendBroadcast() && (
          <div style={{ marginTop: 12 }}>
            <label style={{ display: 'block', fontSize: '0.85rem', color: '#888', marginBottom: 4 }}>
              Email addresses to suppress (comma-separated)
            </label>
            <textarea
              value={suppressEmailList}
              onChange={(e) => setSuppressEmailList(e.target.value)}
              placeholder="user1@example.com, user2@example.com"
              rows={3}
              style={{
                width: '100%',
                background: '#1a1a1a',
                border: '1px solid #2a2a2a',
                borderRadius: 8,
                color: '#fff',
                padding: '10px 12px',
                fontSize: '0.9rem',
                fontFamily: 'inherit',
                resize: 'vertical',
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
